import { apiClient } from '../../../api/client'
import { downloadXlsx } from '../../../lib/download'
import type {
  ActivityCreate,
  ActivityCreated,
  ActivityListItem,
  ActivityLookups,
  ActivityQuery,
  ActivitySummary,
  AttachedFileMeta,
  LookupItem,
  PagedResponse,
} from '../types'

/** Sayfalı/filtreli/aranabilir etkinlik listesi (US1/US3). Null/boş filtreler gönderilmez. */
export async function getActivities(
  query: ActivityQuery,
): Promise<PagedResponse<ActivityListItem>> {
  const params: Record<string, string | number> = {
    page: query.page ?? 0,
    size: query.size ?? 10,
  }
  if (query.statusId != null) params.statusId = query.statusId
  if (query.priority != null) params.priority = query.priority
  if (query.eventTypeId != null) params.eventTypeId = query.eventTypeId
  if (query.invitationTypeId != null) params.invitationTypeId = query.invitationTypeId
  if (query.countryId != null) params.countryId = query.countryId
  if (query.q && query.q.trim()) params.q = query.q.trim()

  const { data } = await apiClient.get<PagedResponse<ActivityListItem>>('/management/activities', {
    params,
  })
  return data
}

/**
 * Etkinlik listesini Excel (.xlsx) olarak indirir (035). Liste ile aynı filtreler; sunucu **tüm** filtreli
 * kayıtları döndürür (sayfalama yok). İndirme mekaniği ve dosya adı ortak {@link downloadXlsx}
 * yardımcısındadır (boş filtreler gönderilmez; ad sunucunun Content-Disposition'ından okunur).
 */
export async function exportActivities(query: ActivityQuery): Promise<void> {
  await downloadXlsx(
    '/management/activities/export',
    {
      statusId: query.statusId,
      priority: query.priority,
      eventTypeId: query.eventTypeId,
      invitationTypeId: query.invitationTypeId,
      countryId: query.countryId,
      q: query.q,
    },
    'etkinlik-listesi.xlsx',
  )
}

/** Katılım durumu (GM kararı) güncelle — 350 Bekliyor · 351 Katılıyor · 352 Katılmıyor. */
export async function updateActivityParticipationStatus(id: number, statusId: number): Promise<void> {
  await apiClient.put(`/management/activities/${id}/participation-status`, { statusId })
}

/** KPI özeti (US4; kapsam-geneli, filtreden bağımsız). */
export async function getActivitySummary(): Promise<ActivitySummary> {
  const { data } = await apiClient.get<ActivitySummary>('/management/activities/summary')
  return data
}

/** Form + filtre kod listeleri (US2/US3). */
export async function getActivityLookups(): Promise<ActivityLookups> {
  const { data } = await apiClient.get<ActivityLookups>('/management/activities/lookups')
  return data
}

/** Seçili ülkenin şehirleri (cascade). */
export async function getCities(countryId: number): Promise<LookupItem[]> {
  const { data } = await apiClient.get<LookupItem[]>('/management/activities/lookups/cities', {
    params: { countryId },
  })
  return data
}

/** Yeni etkinlik oluştur (ana + alt kayıtlar). 201 → { id }. */
export async function createActivity(body: ActivityCreate): Promise<ActivityCreated> {
  const { data } = await apiClient.post<ActivityCreated>('/management/activities', body)
  return data
}

/** Etkinliğe dosya ekle (çoklu, multipart). */
export async function uploadAttachments(
  activityId: number,
  files: File[],
): Promise<AttachedFileMeta[]> {
  const form = new FormData()
  files.forEach((f) => form.append('file', f))
  const { data } = await apiClient.post<AttachedFileMeta[]>(
    `/management/activities/${activityId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}
